import { useState, useMemo, useCallback } from 'react';
import { motion, useAnimation, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Loader } from './Loader';
import { SubscriptionCard } from './profile/SubscriptionCard';
import { CarouselContent } from './cardSlider/CarouselContent';
import { CardModal } from './cardSlider/CardModal';
import { useUserStore } from '../stores/useUserStore';
import { useHapticFeedback } from '../hooks/useHapticFeedback';
import type { Card } from './cardSlider/Card';

interface CardSliderProps {
  cards: Card[];
  loading?: boolean;
}

export default function CardSlider({ cards, loading = false }: CardSliderProps) {
  const { t } = useTranslation();
  const { subscriptionStatus } = useUserStore();
  const { impactOccurred, notificationOccurred } = useHapticFeedback();
  const [activeCard, setActiveCard] = useState<Card | null>(null);
  const [isCarouselActive, setIsCarouselActive] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const controls = useAnimation();
  const navigate = useNavigate();
  const hasActiveSubscription = subscriptionStatus.isActive;

  const cardsWithPremium = useMemo(() => {
    const noDealsAvailable = t('HOME_SCREEN.PREMIUM_DEALS.NO_DEALS_AVAILABLE');
    const noDeals = t('HOME_SCREEN.PREMIUM_DEALS.NO_DEALS');
    
    return cards.map((card) => ({
      ...card,
      isPremium: hasActiveSubscription,
      cardNumber: card.cardNumber || noDealsAvailable,
      type: card.type || noDeals
    }));
  }, [cards, hasActiveSubscription, t]);

  const handleClick = useCallback((card: Card) => {
    if (!hasActiveSubscription) {
      impactOccurred('medium');
      setIsModalOpen(true);
      return;
    }
    
    notificationOccurred('success');
    setActiveCard(card);
    setIsCarouselActive(false);
    navigate(`/asset/${card.id}`);
  }, [hasActiveSubscription, navigate, impactOccurred, notificationOccurred]);

  const handleClose = useCallback(() => {
    setActiveCard(null);
    setIsCarouselActive(true);
  }, []);

  const handleCloseSubscription = useCallback(() => {
    setIsModalOpen(false);
  }, []);

  return (
    <div className="relative w-full m-0 p-0 overflow-hidden min-h-[400px] max-[640px]:min-h-[290px]">
      {loading ? (
        <div className="flex justify-center items-center min-h-[400px] w-full max-[640px]:min-h-[290px]">
          <Loader />
        </div>
      ) : (
        <motion.div layout className="relative w-full m-0 p-0 overflow-hidden min-h-[400px] max-[640px]:min-h-[290px]">
          <div className="relative h-full w-full">
            <CarouselContent
              handleClick={handleClick}
              controls={controls}
              cards={cardsWithPremium}
              isCarouselActive={isCarouselActive}
              defaultPremium={hasActiveSubscription}
            />
          </div>
          <AnimatePresence mode="wait">
            {activeCard && (activeCard.isPremium ?? hasActiveSubscription) && (
              <CardModal key={activeCard.id} card={activeCard} onClose={handleClose} />
            )}
          </AnimatePresence>
          <AnimatePresence>
            {isModalOpen && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.2 }}
                className="fixed inset-0 bg-black/50 flex items-center justify-center z-[1000] backdrop-blur-sm px-3 max-[640px]:px-2"
                onClick={handleCloseSubscription}
              >
                <motion.div
                  initial={{ scale: 0.9, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  exit={{ scale: 0.9, opacity: 0 }}
                  transition={{ duration: 0.2 }}
                  className="bg-tg-bg rounded-[20px] w-full max-w-[480px] max-h-[90vh] overflow-y-auto relative shadow-[0_8px_32px_rgba(0,0,0,0.1)]"
                  onClick={e => e.stopPropagation()}
                >
                  <SubscriptionCard onClose={handleCloseSubscription} />
                </motion.div>
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>
      )}
    </div>
  );
}
